// Phase 2 mock — catalog & pricing data now lives on the vertex.

import api, {
  getProviderPaymentLinkTransportError,
  normalizeProviderPaymentLinkCreateCommand,
  normalizeProviderPaymentLinkQuoteCommand,
} from "./api";
import { filterAndAggregateForPatient } from "../components/peptides/clinicalProfile/peptideOrdersHelpers";
import { PATIENT_WRITABLE_SECTIONS, buildEmptyProfile } from "../data/clinicalProfileData";
import { ensureOrderId } from "../utils/peptideOrderUtils";
import { isOrderBlockedOutput, makeBlockedOrderError } from "../utils/peptideCheckoutSubmission";

// These mocks initialize empty; Phase 2 migration will replace them with real API calls.
let mockCatalog = [];
let mockTierPricing = {};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const getCatalog = async () => {
  await delay(300);
  return [...mockCatalog];
};

// Accepts consolidated JSON payload with tierPricing embedded in variants.
// Splits tier pricing into the pricing store and strips it from the catalog entry.
const extractTierPricing = (product) => {
  const catalogProduct = { ...product };
  catalogProduct.variants = (product.variants || []).map((v) => {
    if (v.tierPricing) {
      mockTierPricing[v.id] = { ...v.tierPricing };
    }
    const { tierPricing, ...rest } = v;
    return rest;
  });
  return catalogProduct;
};

export const createProduct = async (product) => {
  await delay(300);
  const catalogProduct = extractTierPricing(product);
  mockCatalog = [...mockCatalog, catalogProduct];
  return catalogProduct;
};

export const updateProduct = async (productId, updates) => {
  await delay(300);
  const idx = mockCatalog.findIndex((p) => p.id === productId);
  if (idx === -1) return null;
  const catalogProduct = extractTierPricing(updates);
  mockCatalog[idx] = { ...mockCatalog[idx], ...catalogProduct };
  mockCatalog = [...mockCatalog];
  return mockCatalog[idx];
};

export const deleteProduct = async (productId) => {
  await delay(300);
  mockCatalog = mockCatalog.filter((p) => p.id !== productId);
  return true;
};

export const getVariantTierPricing = (variantId) => {
  return mockTierPricing[variantId] || null;
};

export const updateVariantTierPricing = async (variantId, prices) => {
  await delay(100);
  mockTierPricing[variantId] = { ...prices };
  return mockTierPricing[variantId];
};

// Save per-provider custom price overrides to the manufacturer→provider edge.
// Pass an empty customPrices map to clear all overrides for that provider.
// Throws on backend ok:false (e.g. not_authorized, unknown_variant, invalid_amount).
export const saveProviderCustomPricing = async (providerOrgId, customPrices, recurringDefault) => {
  return api.saveProviderCustomPricing(providerOrgId, customPrices, recurringDefault);
};

export const createPeptideOrder = async (orderData, { isManufacturer } = {}) => {
  let data = isManufacturer ? await api.createPeptideOrderManufacturer(orderData) : await api.createPeptideOrder(orderData);
  const output = data?.data?.output || {};
  // Back-order policy block (backend #172): a clean 200 with no order created.
  // Raise a typed error so the checkout surfaces the reason instead of crashing
  // on a missing order.id and showing a generic failure toast.
  if (isOrderBlockedOutput(output)) throw makeBlockedOrderError(output);
  return output.order;
};

// Charges a peptide order's card via Maverick. Returns { success, transactionId?, errorCode?, errorMessage?, order? }.
// On transport failure, throws a sanitized Error — never lets card data ride inside the thrown object.
// `isManufacturer` selects the manufacturer-side Maverick process so a
// manufacturer collecting payment at checkout hits its own charge endpoint
// (same shape, separate auth gate).
export const chargePeptideOrderCard = async (orderId, cardFields, { isManufacturer } = {}) => {
  let res;
  try {
    res = isManufacturer
      ? await api.chargePeptideOrderCardManufacturer(orderId, cardFields)
      : await api.chargePeptideOrderCard(orderId, cardFields);
  } catch {
    // Never rethrow the underlying axios error — its config/message may carry the request body.
    throw new Error("charge_network_error");
  }
  const output = res?.data?.output || {};
  return {
    success: output.success === true,
    transactionId: output.transactionId || null,
    errorCode: output.errorCode || null,
    errorMessage: output.errorMessage || null,
    // The charge script returns the order via toMap(false), which carries
    // neither `id` nor `@rid` — normalize before callers assert on it, or a
    // successful charge reads as a failure and invites a double-charge retry.
    order: ensureOrderId(output.order, orderId) || null,
  };
  // await delay(500);
  // orderCounter++;
  // const order = {
  //   id: `PEP-${orderCounter}`,
  //   ...orderData,
  //   status: "payment_pending",
  //   createdAt: new Date().toISOString(),
  //   updatedAt: new Date().toISOString(),
  // };
  // mockOrders = [order, ...mockOrders];
};

// Charges a PATIENT-placed peptide order's card via Maverick (patient-gated
// proxy). Same normalized return + transport-error swallowing as
// chargePeptideOrderCard — card data must never ride inside a thrown error.
export const chargePatientPeptideOrderCard = async (orderId, cardFields) => {
  let res;
  try {
    res = await api.chargePatientPeptideOrderCard(orderId, cardFields);
  } catch {
    throw new Error("charge_network_error");
  }
  const output = res?.data?.output || {};
  return {
    success: output.success === true,
    transactionId: output.transactionId || null,
    errorCode: output.errorCode || null,
    errorMessage: output.errorMessage || null,
    order: ensureOrderId(output.order, orderId) || null,
  };
};

const validHttpStatus = (value) => Number.isInteger(value) && value >= 100 && value <= 599;

const makePatientPricingError = (details = {}, transportStatus = null) => {
  const code =
    [details.code, details.errorCode, details.error].find((candidate) => typeof candidate === "string" && candidate.length > 0) ||
    "patient_pricing_request_failed";
  const nonSuccessTransportStatus =
    validHttpStatus(transportStatus) && (transportStatus < 200 || transportStatus >= 300) ? transportStatus : null;
  const error = new Error(code);
  error.code = code;
  error.status =
    nonSuccessTransportStatus ??
    (validHttpStatus(details.status) ? details.status : validHttpStatus(transportStatus) ? transportStatus : null);
  try {
    error.current = details.current == null ? null : projectProviderPricingDetail(details.current);
  } catch {
    error.current = null;
  }
  error.freshQuote = details.freshQuote || null;
  error.rowErrors = Array.isArray(details.rowErrors) ? details.rowErrors : [];
  return error;
};

const hasOwn = (source, field) => Object.prototype.hasOwnProperty.call(source || {}, field);

const PROVIDER_EDITOR_ROLES = new Set(["provider", "supervisor", "system"]);
const PROVIDER_SAVE_AUDIT_ACTIONS = new Set(["create", "update", "return_to_retail"]);

const invalidProviderDetail = () => {
  throw new TypeError("invalid_provider_detail_shape");
};

const projectProviderMoneyMap = (source, field) => {
  const value = source?.[field];
  if (!value || typeof value !== "object" || Array.isArray(value)) invalidProviderDetail();
  return Object.fromEntries(
    Object.entries(value).map(([variantId, cents]) => {
      if (variantId.length === 0 || !Number.isSafeInteger(cents) || cents <= 0) invalidProviderDetail();
      return [variantId, cents];
    })
  );
};

const projectProviderLatestEditor = (editor) => {
  if (editor === null) return null;
  if (
    !editor ||
    typeof editor !== "object" ||
    Array.isArray(editor) ||
    typeof editor.displayName !== "string" ||
    editor.displayName.length === 0 ||
    !PROVIDER_EDITOR_ROLES.has(editor.role)
  ) {
    invalidProviderDetail();
  }
  return {
    displayName: editor.displayName,
    role: editor.role,
  };
};

// ArcadeBrain omits null-valued keys from process output entirely, so an
// optional field that is absent means null — not a malformed response. Without
// this, a patient with no pricing record yet (version 0, every patient before
// their first custom price) had pricingId / updatedAt / updatedBy / latestEditor
// arrive as undefined and the whole detail was rejected as invalid, surfacing as
// "Patient pricing could not be loaded safely." The version === 0 branch below
// then asserts those same fields ARE null, so normalising here is what the
// projector already expects rather than a relaxation of it.
const ABSENT_AS_NULL_FIELDS = ["pricingId", "updatedAt", "updatedBy", "latestEditor"];
const withAbsentOptionalsAsNull = (detail) => {
  const normalized = { ...detail };
  ABSENT_AS_NULL_FIELDS.forEach((field) => {
    if (normalized[field] === undefined) normalized[field] = null;
  });
  const limits = normalized.processorLimits;
  if (limits && typeof limits === "object" && !Array.isArray(limits) && limits.maxTransactionCents === undefined) {
    normalized.processorLimits = { ...limits, maxTransactionCents: null };
  }
  return normalized;
};

const projectProviderPricingDetail = (detail) => {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) invalidProviderDetail();
  detail = withAbsentOptionalsAsNull(detail);
  const pricingId =
    detail.pricingId === null
      ? null
      : typeof detail.pricingId === "string" && detail.pricingId.length > 0
        ? detail.pricingId
        : invalidProviderDetail();
  if (!Number.isSafeInteger(detail.version) || detail.version < 0) invalidProviderDetail();
  if (typeof detail.catalogVersion !== "string" || detail.catalogVersion.length === 0) invalidProviderDetail();
  const updatedAt =
    detail.updatedAt === null
      ? null
      : Number.isSafeInteger(detail.updatedAt) && detail.updatedAt > 0
        ? detail.updatedAt
        : invalidProviderDetail();
  const updatedBy =
    detail.updatedBy === null
      ? null
      : typeof detail.updatedBy === "string" && detail.updatedBy.length > 0
        ? detail.updatedBy
        : invalidProviderDetail();
  if (typeof detail.historyAvailable !== "boolean") invalidProviderDetail();
  if (
    !detail.capabilities ||
    typeof detail.capabilities !== "object" ||
    Array.isArray(detail.capabilities) ||
    typeof detail.capabilities.canWritePatientPricing !== "boolean" ||
    typeof detail.capabilities.canViewPatientPricingHistory !== "boolean"
  ) {
    invalidProviderDetail();
  }
  if (!detail.processorLimits || typeof detail.processorLimits !== "object" || Array.isArray(detail.processorLimits)) {
    invalidProviderDetail();
  }
  const maxTransactionCents = detail.processorLimits.maxTransactionCents;
  if (maxTransactionCents !== null && (!Number.isSafeInteger(maxTransactionCents) || maxTransactionCents <= 0)) {
    invalidProviderDetail();
  }

  const customPrices = projectProviderMoneyMap(detail, "customPrices");
  const effectiveFloors = projectProviderMoneyMap(detail, "effectiveFloors");
  const clinicRetailPrices = projectProviderMoneyMap(detail, "clinicRetailPrices");
  const latestEditor = projectProviderLatestEditor(detail.latestEditor);

  if (Object.values(effectiveFloors).some((floorCents) => floorCents <= 100)) {
    invalidProviderDetail();
  }
  // Clinic retail is a DISPLAY FALLBACK the client never enforces, and the
  // process returns DoctorOffice.peptidePatientPricing verbatim while building
  // effectiveFloors only from variants whose provider cost resolves at this
  // provider's tier. Those two sets legitimately differ — a retail price on a
  // variant the manufacturer never priced, or one left under its floor by a
  // later cost increase, is stale clinic setup, not a malformed response.
  // Rejecting the whole detail over one such entry took patient pricing down
  // for the entire organization: every provider WITH retail pricing set saw
  // "Patient pricing could not be loaded safely." while providers with an empty
  // retail map worked. Drop the unusable entry instead. The editor already
  // renders a row with no floor as "minimum unavailable" and a row with no
  // retail as "No clinic retail price", so this fails closed per variant rather
  // than carrying through a price the client could not validate.
  const usableClinicRetailPrices = {};
  Object.entries(clinicRetailPrices).forEach(([variantId, retailCents]) => {
    const floorCents = effectiveFloors[variantId];
    if (!Number.isSafeInteger(floorCents) || floorCents <= 100 || retailCents < floorCents) return;
    // A retail price the merchant account could not charge must not be offered
    // as the fallback price either.
    if (maxTransactionCents !== null && retailCents > maxTransactionCents) return;
    usableClinicRetailPrices[variantId] = retailCents;
  });
  // A custom price is validated against the provider-cost FLOOR, not against
  // clinic retail — the custom price replaces retail entirely, so a clinic that
  // has not set a retail map is not a reason to reject the record. Requiring it
  // here meant that saving a price permanently invalidated the whole detail for
  // such a clinic: the card loaded before the save and errored afterwards.
  Object.entries(customPrices).forEach(([variantId, customCents]) => {
    const floorCents = effectiveFloors[variantId];
    if (!Number.isSafeInteger(floorCents) || floorCents <= 100 || customCents < floorCents) {
      invalidProviderDetail();
    }
  });
  // A STORED custom price over the ceiling stays fatal: unlike retail it has no
  // safe fallback state, and dropping it would show a patient as paying retail
  // when the record says otherwise.
  if (
    maxTransactionCents !== null &&
    Object.values(customPrices).some((unitPriceCents) => unitPriceCents > maxTransactionCents)
  ) {
    invalidProviderDetail();
  }

  if (detail.version === 0) {
    if (
      pricingId !== null ||
      Object.keys(customPrices).length !== 0 ||
      updatedAt !== null ||
      updatedBy !== null ||
      latestEditor !== null ||
      detail.historyAvailable !== false
    ) {
      invalidProviderDetail();
    }
  } else if (pricingId === null || updatedAt === null || updatedBy === null || latestEditor === null) {
    invalidProviderDetail();
  }

  return {
    pricingId,
    version: detail.version,
    customPrices,
    effectiveFloors,
    clinicRetailPrices: usableClinicRetailPrices,
    catalogVersion: detail.catalogVersion,
    updatedAt,
    updatedBy,
    latestEditor,
    historyAvailable: detail.historyAvailable,
    capabilities: {
      canWritePatientPricing: detail.capabilities.canWritePatientPricing,
      canViewPatientPricingHistory: detail.capabilities.canViewPatientPricingHistory,
      canCreatePatientPaymentLink: detail.capabilities.canCreatePatientPaymentLink === true,
    },
    processorLimits: {
      maxTransactionCents,
    },
  };
};

const equalProviderMoneyMaps = (left, right) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length && leftKeys.every((variantId) => left[variantId] === right[variantId]);
};

const projectProviderPricingSaveDetail = (detail, command) => {
  const safe = projectProviderPricingDetail(detail);
  const commandCustomPrices = projectProviderMoneyMap(command, "customPrices");
  if (!Number.isSafeInteger(command?.expectedVersion) || command.expectedVersion < 0) invalidProviderDetail();
  const audit = detail?.audit;
  const hasCustomPrices = Object.keys(safe.customPrices).length > 0;
  const expectedAction = hasCustomPrices ? (command.expectedVersion === 0 ? "create" : "update") : "return_to_retail";
  if (
    !audit ||
    typeof audit !== "object" ||
    Array.isArray(audit) ||
    !PROVIDER_SAVE_AUDIT_ACTIONS.has(audit.action) ||
    audit.action !== expectedAction ||
    !Number.isSafeInteger(audit.fromVersion) ||
    audit.fromVersion !== command.expectedVersion ||
    !Number.isSafeInteger(audit.toVersion) ||
    audit.toVersion !== audit.fromVersion + 1 ||
    audit.toVersion !== safe.version ||
    !Number.isSafeInteger(audit.createdAt) ||
    audit.createdAt <= 0 ||
    audit.createdAt !== safe.updatedAt ||
    !equalProviderMoneyMaps(safe.customPrices, commandCustomPrices)
  ) {
    invalidProviderDetail();
  }
  return {
    ...safe,
    audit: {
      action: audit.action,
      fromVersion: audit.fromVersion,
      toVersion: audit.toVersion,
      createdAt: audit.createdAt,
    },
  };
};

const requireStringField = (source, field) => {
  if (typeof source?.[field] !== "string" || source[field].length === 0) throw new TypeError("invalid_public_shape");
  return source[field];
};

const copyOptionalNullableStringField = (source, safe, field) => {
  if (!hasOwn(source, field)) return;
  if (source[field] !== null && (typeof source[field] !== "string" || source[field].length === 0)) {
    throw new TypeError("invalid_public_shape");
  }
  safe[field] = source[field];
};

const requireIntegerField = (source, field, minimum = 0) => {
  if (!Number.isSafeInteger(source?.[field]) || source[field] < minimum) throw new TypeError("invalid_public_shape");
  return source[field];
};

const projectPatientPaymentLine = (line) => {
  if (!line || typeof line !== "object" || Array.isArray(line)) throw new TypeError("invalid_public_shape");
  const quantity = requireIntegerField(line, "quantity", 1);
  const patientPriceCents = requireIntegerField(line, "patientPriceCents", 1);
  const lineTotalCents = requireIntegerField(line, "lineTotalCents", 1);
  const calculatedLineTotal = patientPriceCents * quantity;
  if (!Number.isSafeInteger(calculatedLineTotal) || calculatedLineTotal !== lineTotalCents) {
    throw new TypeError("invalid_public_shape");
  }
  const safe = {
    variantId: requireStringField(line, "variantId"),
    quantity,
    patientPriceCents,
    lineTotalCents,
  };
  ["productName", "variantName", "label", "dosage"].forEach((field) => copyOptionalNullableStringField(line, safe, field));
  return safe;
};

const projectPatientLinesAndSubtotal = (source) => {
  if (!Array.isArray(source?.lines) || source.lines.length === 0) throw new TypeError("invalid_public_shape");
  const lines = source.lines.map(projectPatientPaymentLine);
  const linesTotal = lines.reduce((total, line) => {
    const next = total + line.lineTotalCents;
    if (!Number.isSafeInteger(next)) throw new TypeError("invalid_public_shape");
    return next;
  }, 0);
  const productSubtotalCents = requireIntegerField(source, "productSubtotalCents", 1);
  if (linesTotal !== productSubtotalCents) throw new TypeError("invalid_public_shape");
  return { lines, productSubtotalCents };
};

const projectPatientQuote = (quote) => {
  if (!quote || typeof quote !== "object") return null;
  if (Array.isArray(quote)) throw new TypeError("invalid_public_shape");
  const { lines, productSubtotalCents } = projectPatientLinesAndSubtotal(quote);
  return {
    pricingQuoteId: requireStringField(quote, "pricingQuoteId"),
    quoteExpiresAt: requireIntegerField(quote, "quoteExpiresAt"),
    productSubtotalCents,
    pricingVersion: requireStringField(quote, "pricingVersion"),
    orderContext: requireStringField(quote, "orderContext"),
    lines,
  };
};

export const MAX_PROVIDER_PAYMENT_LINK_QUOTE_TTL_MS = 15 * 60 * 1000;
export const MAX_PROVIDER_PAYMENT_LINK_TTL_MS = 72 * 60 * 60 * 1000;
const PROVIDER_PAYMENT_LINK_SAFE_STRING_MAX_LENGTH = 512;
const PROVIDER_PAYMENT_LINK_TOKEN_PATH_PATTERN = /^\/p\/[A-Za-z0-9_-]+$/;
const hasProviderPaymentLinkControlCharacter = (value) =>
  [...value].some((character) => {
    const codePoint = character.codePointAt(0);
    return (
      codePoint <= 0x1f ||
      (codePoint >= 0x7f && codePoint <= 0x9f) ||
      codePoint === 0x61c ||
      (codePoint >= 0x200b && codePoint <= 0x200f) ||
      (codePoint >= 0x202a && codePoint <= 0x202e) ||
      codePoint === 0x2060 ||
      (codePoint >= 0x2066 && codePoint <= 0x2069) ||
      codePoint === 0xfeff
    );
  });
const QUOTE_PAYMENT_LINK_CODES = new Set([
  "patient_payment_quote_not_authorized",
  "patient_relationship_not_found",
  "patient_identity_conflict",
  "pricing_unavailable",
  "variant_unavailable",
  "shipping_method_unavailable",
  "shipping_address_unavailable",
  "merchant_limit_unavailable",
  "amount_exceeds_processor_limit",
]);
const CREATE_PAYMENT_LINK_CODES = new Set([
  "patient_payment_link_not_authorized",
  "patient_relationship_not_found",
  "patient_identity_conflict",
  "pricing_changed",
  "pricing_unavailable",
  "variant_unavailable",
  "shipping_method_unavailable",
  "shipping_address_unavailable",
  "merchant_limit_unavailable",
  "amount_exceeds_processor_limit",
  "payment_link_request_conflict",
]);

const invalidProviderPaymentLinkShape = () => {
  throw new TypeError("invalid_provider_payment_link_shape");
};

const requireProviderPaymentLinkString = (source, field, maximumLength = PROVIDER_PAYMENT_LINK_SAFE_STRING_MAX_LENGTH) => {
  const value = source?.[field];
  if (
    typeof value !== "string" ||
    value.trim().length === 0 ||
    value.length > maximumLength ||
    hasProviderPaymentLinkControlCharacter(value)
  ) {
    invalidProviderPaymentLinkShape();
  }
  return value;
};

const requireProviderPaymentLinkNullableString = (source, field) => {
  if (!hasOwn(source, field)) invalidProviderPaymentLinkShape();
  if (source[field] === null) return null;
  return requireProviderPaymentLinkString(source, field);
};

const requireProviderPaymentLinkInteger = (source, field, minimum = 0) => {
  const value = source?.[field];
  if (!Number.isSafeInteger(value) || value < minimum) invalidProviderPaymentLinkShape();
  return value;
};

const requireFutureExpiration = (source, field, now, maximumTtl) => {
  const expiration = requireProviderPaymentLinkInteger(source, field, 1);
  const latest = now + maximumTtl;
  if (!Number.isSafeInteger(latest) || expiration <= now || expiration > latest) invalidProviderPaymentLinkShape();
  return expiration;
};

const projectProviderPaymentLinkLine = (line) => {
  if (!line || typeof line !== "object" || Array.isArray(line)) invalidProviderPaymentLinkShape();
  const quantity = requireProviderPaymentLinkInteger(line, "quantity", 1);
  const patientPriceCents = requireProviderPaymentLinkInteger(line, "patientPriceCents", 1);
  const lineTotalCents = requireProviderPaymentLinkInteger(line, "lineTotalCents", 1);
  const calculatedTotal = patientPriceCents * quantity;
  if (!Number.isSafeInteger(calculatedTotal) || calculatedTotal !== lineTotalCents) invalidProviderPaymentLinkShape();
  return {
    variantId: requireProviderPaymentLinkString(line, "variantId", 256),
    productName: requireProviderPaymentLinkNullableString(line, "productName"),
    variantName: requireProviderPaymentLinkNullableString(line, "variantName"),
    label: requireProviderPaymentLinkString(line, "label"),
    dosage: requireProviderPaymentLinkNullableString(line, "dosage"),
    quantity,
    patientPriceCents,
    lineTotalCents,
  };
};

const bindProviderPaymentLinkQuote = (lines, shippingMethodId, command) => {
  if (shippingMethodId !== command.shippingMethodId || lines.length !== command.items.length) {
    invalidProviderPaymentLinkShape();
  }
  const requestedItems = new Map(command.items.map((item) => [item.variantId, item.quantity]));
  const responseIds = new Set();
  lines.forEach((line) => {
    if (responseIds.has(line.variantId) || requestedItems.get(line.variantId) !== line.quantity) {
      invalidProviderPaymentLinkShape();
    }
    responseIds.add(line.variantId);
  });
  if (responseIds.size !== requestedItems.size) invalidProviderPaymentLinkShape();
};

const projectProviderPaymentLinkQuote = (quote, command, now) => {
  if (!quote || typeof quote !== "object" || Array.isArray(quote)) invalidProviderPaymentLinkShape();
  if (quote.orderContext !== "provider_patient_payment_link") invalidProviderPaymentLinkShape();
  if (!Array.isArray(quote.lines) || quote.lines.length === 0) invalidProviderPaymentLinkShape();
  const lines = quote.lines.map(projectProviderPaymentLinkLine);
  const linesTotal = lines.reduce((sum, line) => {
    const next = sum + line.lineTotalCents;
    if (!Number.isSafeInteger(next)) invalidProviderPaymentLinkShape();
    return next;
  }, 0);
  const productSubtotalCents = requireProviderPaymentLinkInteger(quote, "productSubtotalCents", 1);
  if (linesTotal !== productSubtotalCents) invalidProviderPaymentLinkShape();
  if (!quote.shippingMethod || typeof quote.shippingMethod !== "object" || Array.isArray(quote.shippingMethod)) {
    invalidProviderPaymentLinkShape();
  }
  const shippingMethod = {
    id: requireProviderPaymentLinkString(quote.shippingMethod, "id", 256),
    label: requireProviderPaymentLinkString(quote.shippingMethod, "label"),
  };
  bindProviderPaymentLinkQuote(lines, shippingMethod.id, command);
  const shippingCents = requireProviderPaymentLinkInteger(quote, "shippingCents");
  const amountDueCents = requireProviderPaymentLinkInteger(quote, "amountDueCents", 1);
  const calculatedAmountDue = productSubtotalCents + shippingCents;
  if (!Number.isSafeInteger(calculatedAmountDue) || calculatedAmountDue !== amountDueCents) {
    invalidProviderPaymentLinkShape();
  }
  return {
    pricingQuoteId: requireProviderPaymentLinkString(quote, "pricingQuoteId", 256),
    quoteExpiresAt: requireFutureExpiration(quote, "quoteExpiresAt", now, MAX_PROVIDER_PAYMENT_LINK_QUOTE_TTL_MS),
    pricingVersion: requireProviderPaymentLinkString(quote, "pricingVersion"),
    orderContext: "provider_patient_payment_link",
    productSubtotalCents,
    shippingMethod,
    shippingCents,
    amountDueCents,
    lines,
  };
};

const approvedProviderPaymentLinkOrigin = () => {
  const configured = process.env.REACT_APP_PATIENT_PAYMENT_LINK_ORIGIN || "https://pay.lucas.health";
  if (configured !== configured.trim()) invalidProviderPaymentLinkShape();
  let origin;
  try {
    origin = new URL(configured);
  } catch {
    invalidProviderPaymentLinkShape();
  }
  if (
    configured !== origin.origin ||
    origin.protocol !== "https:" ||
    origin.username !== "" ||
    origin.password !== "" ||
    origin.search !== "" ||
    origin.hash !== "" ||
    origin.pathname !== "/"
  ) {
    invalidProviderPaymentLinkShape();
  }
  return origin.origin;
};

const projectProviderPaymentLinkCreateSuccess = (paymentLink, command, now) => {
  if (!paymentLink || typeof paymentLink !== "object" || Array.isArray(paymentLink)) {
    invalidProviderPaymentLinkShape();
  }
  const pricingQuoteId = requireProviderPaymentLinkString(paymentLink, "pricingQuoteId", 256);
  if (pricingQuoteId !== command.pricingQuoteId) invalidProviderPaymentLinkShape();
  if (paymentLink.payerType !== "patient" || paymentLink.status !== "payment_pending") {
    invalidProviderPaymentLinkShape();
  }
  const paymentUrl = requireProviderPaymentLinkString(paymentLink, "paymentUrl", 2048);
  if (paymentUrl !== paymentUrl.trim()) invalidProviderPaymentLinkShape();
  const approvedOrigin = approvedProviderPaymentLinkOrigin();
  const tokenPrefix = `${approvedOrigin}/p/`;
  const rawToken = paymentUrl.startsWith(tokenPrefix) ? paymentUrl.slice(tokenPrefix.length) : "";
  if (!/^[A-Za-z0-9_-]+$/.test(rawToken)) invalidProviderPaymentLinkShape();
  let parsedPaymentUrl;
  try {
    parsedPaymentUrl = new URL(paymentUrl);
  } catch {
    invalidProviderPaymentLinkShape();
  }
  if (
    parsedPaymentUrl.protocol !== "https:" ||
    parsedPaymentUrl.origin !== approvedOrigin ||
    parsedPaymentUrl.username !== "" ||
    parsedPaymentUrl.password !== "" ||
    parsedPaymentUrl.search !== "" ||
    parsedPaymentUrl.hash !== "" ||
    !PROVIDER_PAYMENT_LINK_TOKEN_PATH_PATTERN.test(parsedPaymentUrl.pathname)
  ) {
    invalidProviderPaymentLinkShape();
  }
  return {
    orderId: requireProviderPaymentLinkString(paymentLink, "orderId", 256),
    paymentUrl,
    paymentLinkExpiresAt: requireFutureExpiration(paymentLink, "paymentLinkExpiresAt", now, MAX_PROVIDER_PAYMENT_LINK_TTL_MS),
    pricingQuoteId,
    payerType: "patient",
    status: "payment_pending",
  };
};

const isCancellation = (error) => error?.name === "AbortError" || error?.code === "ERR_CANCELED";
const makeDetachedCancellation = () => {
  const cancellation = new Error("canceled");
  cancellation.name = "AbortError";
  cancellation.code = "ERR_CANCELED";
  return cancellation;
};

const makeProviderPaymentLinkError = ({
  details,
  transportStatus,
  allowlist,
  fallbackCode,
  command,
  allowFreshQuote = false,
}) => {
  const candidate = [details?.code, details?.errorCode].find((code) => typeof code === "string" && allowlist.has(code));
  const code = candidate || fallbackCode;
  const nonSuccessTransportStatus =
    validHttpStatus(transportStatus) && (transportStatus < 200 || transportStatus >= 300) ? transportStatus : null;
  const status = nonSuccessTransportStatus ?? (validHttpStatus(details?.status) ? details.status : null);
  const error = new Error(code);
  error.code = code;
  error.status = status;
  if (allowFreshQuote && code === "pricing_changed") {
    try {
      error.freshQuote = projectProviderPaymentLinkQuote(details?.freshQuote, command, Date.now());
    } catch {
      error.freshQuote = null;
    }
  }
  return error;
};

const PUBLIC_MACHINE_CODE_PATTERN = /^[a-z][a-z0-9_]{2,63}$/;
const APPROVED_PUBLIC_PAYMENT_CODES = new Set([
  "payment_link_expired",
  "payment_link_not_found",
  "payment_link_revoked",
  "payment_link_already_paid",
  "payment_link_cancelled",
  "pricing_changed",
  "pricing_unavailable",
  "variant_unavailable",
]);
const APPROVED_PUBLIC_ROW_CODES = new Set(["pricing_changed", "variant_unavailable"]);

const approvedPublicCode = (candidate, allowlist) =>
  typeof candidate === "string" && PUBLIC_MACHINE_CODE_PATTERN.test(candidate) && allowlist.has(candidate);

const projectPatientRowErrors = (rowErrors) =>
  Array.isArray(rowErrors)
    ? rowErrors
        .filter(
          (rowError) => rowError && typeof rowError === "object" && approvedPublicCode(rowError.code, APPROVED_PUBLIC_ROW_CODES)
        )
        .map((rowError) => ({
          ...(typeof rowError.variantId === "string" ? { variantId: rowError.variantId } : {}),
          code: rowError.code,
        }))
    : [];

const makePatientPublicPaymentError = (details, fallbackStatus, fallbackCode) => {
  const code =
    [details?.code, details?.errorCode].find((candidate) => approvedPublicCode(candidate, APPROVED_PUBLIC_PAYMENT_CODES)) ||
    fallbackCode;
  const error = new Error(code);
  error.code = code;
  error.status = Number.isInteger(details?.status) ? details.status : Number.isInteger(fallbackStatus) ? fallbackStatus : null;
  try {
    error.freshQuote = projectPatientQuote(details?.freshQuote);
  } catch {
    error.freshQuote = null;
  }
  error.rowErrors = projectPatientRowErrors(details?.rowErrors);
  return error;
};

const projectPatientPaymentLink = (paymentLink) => {
  if (!paymentLink || typeof paymentLink !== "object" || Array.isArray(paymentLink)) {
    throw new TypeError("invalid_public_shape");
  }
  const { lines, productSubtotalCents } = projectPatientLinesAndSubtotal(paymentLink);
  const shippingCents = requireIntegerField(paymentLink, "shippingCents");
  const amountDueCents = requireIntegerField(paymentLink, "amountDueCents", 1);
  const calculatedAmountDue = productSubtotalCents + shippingCents;
  if (!Number.isSafeInteger(calculatedAmountDue) || calculatedAmountDue !== amountDueCents) {
    throw new TypeError("invalid_public_shape");
  }
  const status = requireStringField(paymentLink, "status");
  if (status !== "payment_pending") throw new TypeError("invalid_public_shape");
  const safe = {
    orderId: requireStringField(paymentLink, "orderId"),
    status,
    productSubtotalCents,
    shippingCents,
    amountDueCents,
    lines,
  };
  ["manufacturerName", "manufacturerLogoUrl"].forEach((field) => copyOptionalNullableStringField(paymentLink, safe, field));
  safe.paymentLinkExpiresAt = requireIntegerField(paymentLink, "paymentLinkExpiresAt", 1);
  safe.pricingQuoteId = requireStringField(paymentLink, "pricingQuoteId");
  safe.payerType = requireStringField(paymentLink, "payerType");
  if (safe.payerType !== "patient") throw new TypeError("invalid_public_shape");
  return safe;
};

const projectPaidConfirmation = (confirmation) => {
  if (!confirmation || typeof confirmation !== "object" || Array.isArray(confirmation)) {
    throw new TypeError("invalid_public_shape");
  }
  const status = requireStringField(confirmation, "status");
  const payerType = requireStringField(confirmation, "payerType");
  if (status !== "paid" || payerType !== "patient") throw new TypeError("invalid_public_shape");
  const productSubtotalCents = requireIntegerField(confirmation, "productSubtotalCents", 1);
  const shippingCents = requireIntegerField(confirmation, "shippingCents");
  const amountPaidCents = requireIntegerField(confirmation, "amountPaidCents", 1);
  const calculatedAmountPaid = productSubtotalCents + shippingCents;
  if (!Number.isSafeInteger(calculatedAmountPaid) || calculatedAmountPaid !== amountPaidCents) {
    throw new TypeError("invalid_public_shape");
  }
  return {
    orderId: requireStringField(confirmation, "orderId"),
    status,
    paidAt: requireIntegerField(confirmation, "paidAt", 1),
    payerType,
    productSubtotalCents,
    shippingCents,
    amountPaidCents,
    receiptReference: requireStringField(confirmation, "receiptReference"),
  };
};

const HISTORY_ACTIONS = new Set(["create", "update", "return_to_retail", "floor_adjustment", "identity_link"]);
const HISTORY_EDITOR_ROLES = new Set(["provider", "supervisor", "system"]);

const requireNullablePositiveInteger = (value) => {
  if (value === null) return null;
  if (!Number.isSafeInteger(value) || value <= 0) throw new TypeError("invalid_history_shape:nullable_money");
  return value;
};

const invalidHistoryShape = (reason) => {
  throw new TypeError(`invalid_history_shape:${reason}`);
};

// /web/run-process serializes nested arrays as objects with dense numeric keys.
// Accept that exact transport shape while continuing to reject sparse or
// property-bearing objects rather than treating arbitrary objects as arrays.
const normalizeHistoryArray = (value, maximumLength, reason) => {
  let items;
  if (Array.isArray(value)) {
    items = value;
  } else if (value && typeof value === "object") {
    const keys = Object.keys(value);
    if (!keys.every((key, index) => key === String(index))) invalidHistoryShape(reason);
    items = keys.map((key) => value[key]);
  } else {
    invalidHistoryShape(reason);
  }
  if (items.length > maximumLength) invalidHistoryShape(reason);
  return items;
};

const projectPatientPricingHistory = (history) => {
  if (history == null) invalidHistoryShape("history_missing");
  if (typeof history !== "object" || Array.isArray(history)) invalidHistoryShape("history_type");
  const historyEntries = normalizeHistoryArray(history.entries, 50, "entries");
  // ArcadeBrain drops null-valued fields while serializing process output.
  // These fields are nullable by contract, so restore only absent values to
  // null before validating; present malformed values still fail closed.
  const nextCursor = history.nextCursor ?? null;
  if (nextCursor !== null && (typeof nextCursor !== "string" || nextCursor.length === 0 || nextCursor.length > 2048)) {
    invalidHistoryShape("cursor");
  }
  const entries = historyEntries.map((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry) || !HISTORY_ACTIONS.has(entry.action)) {
      invalidHistoryShape("entry");
    }
    if (!Number.isSafeInteger(entry.fromVersion) || entry.fromVersion < 0) {
      invalidHistoryShape("from_version");
    }
    if (!Number.isSafeInteger(entry.toVersion) || entry.toVersion !== entry.fromVersion + 1) {
      invalidHistoryShape("version_transition");
    }
    if (!Number.isSafeInteger(entry.createdAt) || entry.createdAt <= 0) {
      invalidHistoryShape("created_at");
    }
    if (
      !entry.editor ||
      typeof entry.editor !== "object" ||
      Array.isArray(entry.editor) ||
      typeof entry.editor.displayName !== "string" ||
      entry.editor.displayName.length === 0 ||
      !HISTORY_EDITOR_ROLES.has(entry.editor.role)
    ) {
      invalidHistoryShape("entry_metadata");
    }
    const historyChanges = normalizeHistoryArray(entry.changes, 500, "changes");
    if (entry.action !== "identity_link" && historyChanges.length === 0) invalidHistoryShape("entry_metadata");
    return {
      action: entry.action,
      fromVersion: entry.fromVersion,
      toVersion: entry.toVersion,
      createdAt: entry.createdAt,
      editor: {
        displayName: entry.editor.displayName,
        role: entry.editor.role,
      },
      changes: historyChanges.map((change) => {
        const variantLabel = change?.variantLabel ?? null;
        if (
          !change ||
          typeof change !== "object" ||
          Array.isArray(change) ||
          typeof change.variantId !== "string" ||
          change.variantId.length === 0 ||
          (variantLabel !== null && (typeof variantLabel !== "string" || variantLabel.length === 0))
        ) {
          invalidHistoryShape("change");
        }
        const beforeCents = requireNullablePositiveInteger(change.beforeCents ?? null);
        const afterCents = requireNullablePositiveInteger(change.afterCents ?? null);
        if (beforeCents === null && afterCents === null) invalidHistoryShape("empty_change");
        return {
          variantId: change.variantId,
          variantLabel,
          beforeCents,
          afterCents,
        };
      }),
    };
  });
  for (let index = 1; index < entries.length; index += 1) {
    if (entries[index - 1].createdAt < entries[index].createdAt || entries[index - 1].fromVersion !== entries[index].toVersion) {
      invalidHistoryShape("order");
    }
  }
  return {
    entries,
    nextCursor,
  };
};

const APPROVED_HISTORY_CODES = new Set([
  "patient_pricing_history_identity_invalid",
  "patient_pricing_history_limit_invalid",
  "patient_pricing_history_cursor_invalid",
  "pricing_auth_required",
  "patient_relationship_forbidden",
  "pricing_unavailable",
]);

const makePatientPricingHistoryError = (details = {}, transportStatus = null, localCode = null) => {
  const code =
    [details.code, details.errorCode, localCode].find(
      (candidate) => typeof candidate === "string" && APPROVED_HISTORY_CODES.has(candidate)
    ) || "patient_pricing_history_request_failed";
  const nonSuccessTransportStatus =
    validHttpStatus(transportStatus) && (transportStatus < 200 || transportStatus >= 300) ? transportStatus : null;
  const status =
    nonSuccessTransportStatus ??
    (validHttpStatus(details.status) ? details.status : validHttpStatus(transportStatus) ? transportStatus : null);
  const error = new Error(code);
  error.code = code;
  error.status = status;
  return error;
};

const makeSanitizedPublicError = (code) => {
  const error = new Error(code);
  error.code = code;
  return error;
};

// The sanitized public error deliberately carries no server detail, which also
// meant every projector rejection reached the console as nothing at all — the
// panel just said "could not be loaded safely" with no way to tell which
// assertion tripped. The stack points at the failing line inside the projector,
// so log it outside production only. Never log the payload itself: it is patient
// pricing data.
const logRejectedPatientPricingPayload = (boundary, caught) => {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.warn(`[patient pricing] ${boundary} response failed client validation`, caught);
};

const successfulPatientPricingOutput = (response, payloadKey) => {
  const output = response?.data?.output || {};
  if (output.success !== true) throw makePatientPricingError(output, response?.status ?? null);
  if (payloadKey && output[payloadKey] != null) return output[payloadKey];
  const { success, ...payload } = output;
  return payload;
};

const runNormalizedPatientPricingRequest = async (request, payloadKey) => {
  try {
    return successfulPatientPricingOutput(await request(), payloadKey);
  } catch (caught) {
    const response = caught?.response || caught?.cause?.response;
    const details = response?.data?.output || response?.data || caught || {};
    throw makePatientPricingError(details, response?.status ?? caught?.status ?? null);
  }
};

export const getPatientPeptidePricingDetail = async (identity, options) => {
  const detail = await runNormalizedPatientPricingRequest(() => api.getPatientPeptidePricingDetail(identity, options), "detail");
  try {
    return projectProviderPricingDetail(detail);
  } catch (caught) {
    logRejectedPatientPricingPayload("GET detail", caught);
    throw makeSanitizedPublicError("patient_pricing_detail_response_invalid");
  }
};

export const savePatientPeptidePricingDetail = async (command, options) => {
  const detail = await runNormalizedPatientPricingRequest(() => api.savePatientPeptidePricingDetail(command, options), "detail");
  try {
    return projectProviderPricingSaveDetail(detail, command);
  } catch (caught) {
    logRejectedPatientPricingPayload("SAVE detail", caught);
    throw makeSanitizedPublicError("patient_pricing_save_response_invalid");
  }
};

export const getPatientPeptidePricingHistory = async (command, options) => {
  let response;
  try {
    response = await api.getPatientPeptidePricingHistory(command, options);
  } catch (caught) {
    const transportResponse = caught?.response || caught?.cause?.response;
    const details = transportResponse?.data?.output || transportResponse?.data || {};
    throw makePatientPricingHistoryError(details, transportResponse?.status ?? null, caught?.code || caught?.message || null);
  }
  const output = response?.data?.output || {};
  if (output.success !== true) {
    throw makePatientPricingHistoryError(output, response?.status ?? null);
  }
  try {
    return projectPatientPricingHistory(output.history);
  } catch (caught) {
    logRejectedPatientPricingPayload("GET history", caught);
    throw makeSanitizedPublicError("patient_pricing_history_response_invalid");
  }
};

export const quotePatientPeptidePaymentLink = async (command, options) => {
  const canonicalCommand = normalizeProviderPaymentLinkQuoteCommand(command);
  let response;
  try {
    response = await api.quotePatientPeptidePaymentLink(canonicalCommand, options);
  } catch (caught) {
    if (isCancellation(caught)) throw makeDetachedCancellation();
    const safeTransport = getProviderPaymentLinkTransportError(caught);
    const transportResponse = caught?.response || caught?.cause?.response;
    const details = safeTransport?.output || transportResponse?.data?.output || transportResponse?.data || {};
    throw makeProviderPaymentLinkError({
      details,
      transportStatus: safeTransport?.status ?? transportResponse?.status ?? caught?.status ?? null,
      allowlist: QUOTE_PAYMENT_LINK_CODES,
      fallbackCode: "patient_payment_link_quote_request_failed",
      command: canonicalCommand,
    });
  }
  const output = response?.data?.output || {};
  if (output.success !== true) {
    throw makeProviderPaymentLinkError({
      details: output,
      transportStatus: response?.status ?? null,
      allowlist: QUOTE_PAYMENT_LINK_CODES,
      fallbackCode: "patient_payment_link_quote_request_failed",
      command: canonicalCommand,
    });
  }
  try {
    return projectProviderPaymentLinkQuote(output.quote, canonicalCommand, Date.now());
  } catch {
    throw makeSanitizedPublicError("patient_payment_link_quote_response_invalid");
  }
};

export const createPatientPeptidePaymentLink = async (command, options) => {
  const canonicalCommand = normalizeProviderPaymentLinkCreateCommand(command);
  let response;
  try {
    response = await api.createPatientPeptidePaymentLink(canonicalCommand, options);
  } catch (caught) {
    if (isCancellation(caught)) throw makeDetachedCancellation();
    const safeTransport = getProviderPaymentLinkTransportError(caught);
    const transportResponse = caught?.response || caught?.cause?.response;
    const details = safeTransport?.output || transportResponse?.data?.output || transportResponse?.data || {};
    throw makeProviderPaymentLinkError({
      details,
      transportStatus: safeTransport?.status ?? transportResponse?.status ?? caught?.status ?? null,
      allowlist: CREATE_PAYMENT_LINK_CODES,
      fallbackCode: "patient_payment_link_create_request_failed",
      command: canonicalCommand,
      allowFreshQuote: true,
    });
  }
  const output = response?.data?.output || {};
  if (output.success !== true) {
    throw makeProviderPaymentLinkError({
      details: output,
      transportStatus: response?.status ?? null,
      allowlist: CREATE_PAYMENT_LINK_CODES,
      fallbackCode: "patient_payment_link_create_request_failed",
      command: canonicalCommand,
      allowFreshQuote: true,
    });
  }
  try {
    return projectProviderPaymentLinkCreateSuccess(output.paymentLink, canonicalCommand, Date.now());
  } catch {
    throw makeSanitizedPublicError("patient_payment_link_create_response_invalid");
  }
};

export const revokePatientPeptidePaymentLink = async (orderId, options) =>
  runNormalizedPatientPricingRequest(() => api.revokePatientPeptidePaymentLink(orderId, options));

export const getPatientPeptidePaymentLink = async (token, options) => {
  let response;
  try {
    response = await api.getPatientPeptidePaymentLink(token, options);
  } catch (caught) {
    const response = caught?.response || caught?.cause?.response;
    if (response) {
      const details = response?.data?.output || response?.data || {};
      throw makePatientPublicPaymentError(
        details,
        response?.status ?? caught?.status ?? null,
        "patient_payment_link_request_failed"
      );
    }
    throw makeSanitizedPublicError("patient_payment_link_network_error");
  }

  const output = response?.data?.output || {};
  if (output.success !== true) {
    throw makePatientPublicPaymentError(output, response?.status ?? null, "patient_payment_link_request_failed");
  }
  try {
    return projectPatientPaymentLink(output.paymentLink);
  } catch {
    throw makeSanitizedPublicError("patient_payment_link_response_invalid");
  }
};

export const chargePatientPeptidePaymentLink = async (token, cardFields, idempotencyKey, options) => {
  let response;
  try {
    response = await api.chargePatientPeptidePaymentLink(token, cardFields, idempotencyKey, options);
  } catch (caught) {
    const response = caught?.response || caught?.cause?.response;
    if (response) {
      const details = response?.data?.output || response?.data || {};
      throw makePatientPublicPaymentError(
        details,
        response?.status ?? caught?.status ?? null,
        "patient_payment_charge_request_failed"
      );
    }
    throw new Error("charge_network_error");
  }

  const output = response?.data?.output || {};
  if (output.success !== true) {
    throw makePatientPublicPaymentError(output, response?.status ?? null, "patient_payment_charge_request_failed");
  }
  try {
    return {
      success: true,
      paidConfirmation: projectPaidConfirmation(output.paidConfirmation),
    };
  } catch {
    throw makeSanitizedPublicError("patient_payment_charge_response_invalid");
  }
};

export const getPeptideOrders = async ({ isManufacturer, currentOrgSlug } = {}) => {
  let data = isManufacturer ? await api.getPeptideOrdersManufacturer() : await api.getPeptideOrders();
  const orders = data.data.output.orders || [];

  // Backfill `source` on each order using organizationId-based inference.
  //
  // TODO(BE): include `source` (and `assignedProviderId`, `placingProviderName`,
  // `placedByDoctorId`) in the list-orders projection. Once shipped, the
  // `if (order?.source)` early-return below will gracefully no-op and the rest
  // of this inference block becomes dead code that can be deleted.
  //
  // Why this exists: the create-order BE endpoint persists `source` correctly
  // (verified — manufacturer-placed orders come back with source="manufacturer"
  // on the create response), but the *list-orders* BE endpoint doesn't include
  // `source` in its projection (along with assignedProviderId/
  // placingProviderName/placedByDoctorId). Without `source`, getOrderOrigin()
  // returns "unknown" and orders only show up in the kanban's "All" filter —
  // never in My/Provider/Patient filters. Until the BE list projection is
  // expanded, we infer it client-side from the one classification signal the
  // list response DOES keep: organizationId.
  //
  // Inference rule:
  //   - placedByPatient === true → leave source unset; classifier picks
  //     "patient" from the placedByPatient flag directly (first check in
  //     getOrderOrigin), so we don't need to invent a source value here.
  //   - Manufacturer viewer:
  //       org match → "manufacturer" (they placed it)
  //       org mismatch → "provider" (one of their providers placed it; the BE
  //         list scope for a manufacturer is exactly self + providers + patient
  //         flows, and patients are excluded above by placedByPatient)
  //   - Provider viewer:
  //       org match → "provider" (they placed it)
  //       org mismatch → leave source UNSET so the order falls through to
  //         "unknown" and only appears in the All chip. We don't blindly tag
  //         it "manufacturer" because for a provider viewer it could just as
  //         likely be a patient-placed order whose placedByPatient flag failed
  //         to round-trip — silently mis-categorizing that into a Manufacturer
  //         chip would be worse than leaving it in All.
  //
  // Skip orders that already carry a real `source` — defensive in case the BE
  // ever starts returning it; we trust upstream over inference.
  //
  // Case-insensitive compare: useAuth.js stores currentOrganizationName in
  // localStorage as toLowerCase() but the in-memory value may keep original
  // case (e.g. "Master-Orders" vs "master-orders"). The BE's organizationId
  // is lowercase. Normalize both sides so a stray capital doesn't silently
  // route every order to the wrong filter. Both currentOrganizationName and
  // organizationId are org *slugs*, not numeric ids — same shape, compare as
  // strings.
  let resolved = orders;
  if (currentOrgSlug) {
    const normalizedViewerOrg = String(currentOrgSlug).toLowerCase();
    resolved = orders.map((order) => {
      // A manufacturer placed this order on THIS clinic's behalf. Tag it so
      // getOrderOrigin routes it into the clinic's provider lanes and the card
      // shows a "From Manufacturer" badge. Computed here because this is where
      // the viewer org (normalizedViewerOrg) is known; leaves `source` intact.
      if (
        !isManufacturer &&
        order?.assignType === "provider" &&
        String(order?.assignedProviderId || "").toLowerCase() === normalizedViewerOrg
      ) {
        return { ...order, providerInbound: true };
      }
      if (order?.source) return order;
      if (order?.placedByPatient === true) return order;
      const placingOrg = String(order?.organizationId || "").toLowerCase();
      const placedByThisViewer = placingOrg === normalizedViewerOrg;
      if (isManufacturer) {
        return { ...order, source: placedByThisViewer ? "manufacturer" : "provider" };
      }
      // Provider viewer: only tag own orders. Mismatches stay unset → "unknown".
      if (placedByThisViewer) return { ...order, source: "provider" };
      return order;
    });
  }

  return resolved;
};

export const addPeptideOrderComment = async (orderId, comment, options = {}) => {
  const { isManufacturer, isPatient, attachments } = options;
  // Non-manufacturer must explicitly route to patient OR provider wrapper —
  // each wrapper stamps a different creator role on the persisted comment.
  // Throwing on missing role surfaces a wiring mistake at the call site
  // instead of silently mislabeling every comment from that handler.
  const res = isManufacturer
    ? await api.addPeptideOrderCommentManufacturer(orderId, comment, attachments)
    : await api.addPeptideOrderComment(orderId, comment, attachments, isPatient ? "patient" : "provider");
  // Guard against {ok:false} responses (e.g. order locked / race condition).
  // Without this, callers do setOrder(undefined) and the page replaces with
  // "Order not found", silently destroying any draft edits in progress.
  // OrderConversationThread.handleSend's catch keeps the textarea text on throw.
  const order = ensureOrderId(res?.data?.output?.order, orderId);
  if (!order) throw new Error("addPeptideOrderComment failed: no order in response");
  // Partial-success surfacing: when ≥1 file fails archive-write the BE saves
  // the comment without those files and returns the rejected entries on
  // `attachmentsDropped: [{ index, name, mimeType, size }]`. Callers fire a
  // warning toast so the user sees the partial save and can re-attach the
  // failed file(s) on a follow-up message.
  const attachmentsDropped = res?.data?.output?.attachmentsDropped || null;
  return { order, attachmentsDropped };
};

// Resolves bytes for a previously-uploaded comment attachment, returned
// base64-encoded inline. Returns { base64, mimeType, originalName, fileName }
// on success. Inline bytes (not a signed URL) because `platform.bucket.signGet`
// is not wired on the assistant-v2 brain; see the mint scripts' header
// comments for the rationale. Re-throws with a normalized `code` so the
// caller can render a
// specific toast — the raw axios error doesn't have stable shape across
// network vs. process failures.
//   - SCAN_PENDING (425): file still being scanned by the platform AV
//   - INFECTED (410): quarantined as infected — file is gone
//   - NOT_FOUND (404): comment / fileName does not exist on this order
//   - UNKNOWN: anything else (network blip, 500, etc.)
export const getPeptideOrderAttachmentUrl = async (orderId, fileName, { isManufacturer, isPatient } = {}) => {
  try {
    return await api.getPeptideOrderCommentAttachmentSignedUrl(orderId, fileName, { isManufacturer, isPatient });
  } catch (err) {
    const status = err?.response?.status;
    let code = "UNKNOWN";
    if (status === 425) code = "SCAN_PENDING";
    else if (status === 410) code = "INFECTED";
    else if (status === 404) code = "NOT_FOUND";
    const normalized = new Error(`getPeptideOrderAttachmentUrl failed: ${code}`);
    normalized.code = code;
    normalized.status = status;
    throw normalized;
  }
};

export const denyPeptideOrderManufacturer = async (orderId, comment) => {
  const res = await api.denyPeptideOrderManufacturer(orderId, comment);
  // Same guard as addPeptideOrderComment — protect callers from setOrder(undefined)
  // when the BE rejects (e.g. not the manufacturer for this order, already denied).
  const order = ensureOrderId(res?.data?.output?.order, orderId);
  if (!order) throw new Error("denyPeptideOrderManufacturer failed: no order in response");
  return order;
};

export const getPeptideOrder = async (orderId, { isManufacturer } = {}) => {
  let data = isManufacturer ? await api.getPeptideOrderManufacturer(orderId) : await api.getPeptideOrder(orderId);
  return data.data.output.order;
};

export const EMPTY_PATIENT_ORDERS = Object.freeze({
  orders: [],
  totalCount: 0,
  lifetimeCents: 0,
  lastOrderedAt: null,
});

// Provider-only: clinical profiles are a provider concept, so we always hit the
// org-wide provider list and filter by assignedPatientId. Manufacturer orgs do
// not have a clinical-profile surface in v1.
export const getOrdersByPatient = async (patientId) => {
  if (!patientId) return EMPTY_PATIENT_ORDERS;
  const allOrders = await getPeptideOrders();
  return filterAndAggregateForPatient(allOrders, patientId);
};

export const createPaymentIntent = async (amountCents, { isManufacturer, orderId } = {}) => {
  const res = isManufacturer
    ? await api.createPeptidePaymentIntentManufacturer(amountCents, orderId)
    : await api.createPeptidePaymentIntent(amountCents, orderId);
  const output = res.data?.output || {};
  return {
    clientSecret: output.clientSecret,
    paymentIntentId: output.paymentIntentId,
  };
};

export const createDraftOrder = async (draftData, { isManufacturer } = {}) => {
  // await delay(500);
  // orderCounter++;
  const order = {
    // id: `PEP-${orderCounter}`,
    ...draftData,
    status: "draft",
    source: "patient-intake",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  let data = isManufacturer ? await api.createPeptideOrderManufacturer(order) : await api.createPeptideOrder(order);
  return data.data.output.order;
  // mockOrders = [order, ...mockOrders];
  // return order;
};

export const updateDraftOrder = async (orderId, updates, { isManufacturer } = {}) => {
  let order = isManufacturer
    ? await api.updatePeptideOrderManufacturer(orderId, { ...updates, updatedAt: new Date().toISOString() })
    : await api.updatePeptideOrder(orderId, { ...updates, updatedAt: new Date().toISOString() });
  return ensureOrderId(order.data.output.order, orderId);
  // await delay(300);
  // const idx = mockOrders.findIndex((o) => o.id === orderId);
  // if (idx === -1) return null;
  // mockOrders[idx] = { ...mockOrders[idx], ...updates, updatedAt: new Date().toISOString() };
  // return mockOrders[idx];
};

// Persist a small set of fields onto an existing peptide order. Partial /
// merge-style — the BE merges exactly what we send (same contract updateDraftOrder
// relies on), so this never clobbers fields absent from `fields`. Used to stamp
// activityReviewInfo for the org-wide recent-activity "reviewed" state.
//
// Sends ONLY the caller-supplied fields. Don't add ambient extras (e.g. a
// client-stamped `updatedAt`) here — the manufacturer-side Update script lets
// arbitrary unblocked fields through, while the provider wrapper applies a
// whitelist that silently drops anything outside it, so any ambient field would
// persist on one path and vanish on the other. Keep the payload symmetric so
// storage doesn't diverge across roles.
//
// Returns the BE-stamped order (`res.data.output.order`) so callers can hydrate
// local state with the persisted values — important for activityReviewInfo,
// which is re-stamped server-side from session context (reviewedBy /
// reviewedByLabel / reviewedAt) by Peptide Order - Update so the client-supplied
// identity can't be spoofed and the timestamp matches the server clock the
// next reader will compare against. See lucasai-backend PR #105 follow-up.
export const updatePeptideOrderFields = async (orderId, fields, { isManufacturer } = {}) => {
  const res = isManufacturer
    ? await api.updatePeptideOrderManufacturer(orderId, fields)
    : await api.updatePeptideOrder(orderId, fields);
  return ensureOrderId(res.data.output.order, orderId);
};

// Internal staff note (manufacturer only). Append when noteId is omitted,
// edit that note in place when set. Returns the order's full note list
// (latest-updated first) so callers replace state in one round trip. Empty
// text is rejected server-side too — callers should still disable the save
// button on blank input for a friendlier error.
export const savePeptideInternalNote = async (orderId, note, noteId = null) => {
  const res = await api.savePeptideInternalNote(orderId, note, noteId);
  return res.data.output.notes || [];
};

export const finalizeDraftOrder = async (orderId, { isManufacturer } = {}) => {
  let order = isManufacturer
    ? await api.updatePeptideOrderStatusManufacturer(orderId, "payment_pending")
    : await api.updatePeptideOrderStatus(orderId, "payment_pending");
  return ensureOrderId(order.data.output.order, orderId);

  // await delay(400);
  // const idx = mockOrders.findIndex((o) => o.id === orderId);
  // if (idx === -1) return null;
  // mockOrders[idx] = { ...mockOrders[idx], status: "payment_pending", updatedAt: new Date().toISOString() };
  // return mockOrders[idx];
};

export const updatePeptideOrderStatus = async (orderId, newStatus, extra = {}, { isManufacturer } = {}) => {
  let order = isManufacturer
    ? await api.updatePeptideOrderStatusManufacturer(orderId, newStatus, extra)
    : await api.updatePeptideOrderStatus(orderId, newStatus, extra);
  return ensureOrderId(order.data.output.order, orderId);

  // await delay(300);
  // const idx = mockOrders.findIndex((o) => o.id === orderId);
  // if (idx === -1) return null;
  // mockOrders[idx] = { ...mockOrders[idx], ...extra, status: newStatus, updatedAt: new Date().toISOString() };
  // return mockOrders[idx];
};

export const uploadPaymentReceipt = async (orderId, base64Content, paymentMethod = "zelle", { isManufacturer } = {}) => {
  // Route through the dedicated upload process so the bytes actually land in
  // the manufacturer's archive (archive-write) and the order vertex gets a
  // fileName that points at real bytes. The previous implementation stuffed a
  // fabricated fileName onto the order without ever writing to the archive,
  // which is why the get-file path returned an empty base64 on both sides.
  const res = await api.uploadPeptidePaymentReceipt(orderId, base64Content, paymentMethod, { isManufacturer });
  return ensureOrderId(res?.data?.output?.order, orderId);

  // await delay(400);
  // const idx = mockOrders.findIndex((o) => o.id === orderId);
  // if (idx === -1) return null;
  // mockOrders[idx] = {
  //   ...mockOrders[idx],
  //   paymentReceipt: {
  //     fileName: `mock-receipt-${Date.now()}`,
  //     uploadedAt: new Date().toISOString(),
  //     uploadedBy: "mock-provider",
  //     paymentMethod,
  //   },
  //   updatedAt: new Date().toISOString(),
  // };
  // return mockOrders[idx];
};

export const confirmPayment = async (orderId, { isManufacturer } = {}) => {
  let order = isManufacturer
    ? await api.updatePeptideOrderStatusManufacturer(orderId, "confirmed")
    : await api.updatePeptideOrderStatus(orderId, "confirmed");
  return ensureOrderId(order.data.output.order, orderId);

  // await delay(400);
  // const idx = mockOrders.findIndex((o) => o.id === orderId);
  // if (idx === -1) return null;
  // mockOrders[idx] = { ...mockOrders[idx], status: "confirmed", updatedAt: new Date().toISOString() };
  // return mockOrders[idx];
};

export const getManufacturerPickupInfo = async () => {
  try {
    const res = await api.getPeptideManufacturerPickupInfo();
    const output = res?.data?.output;
    if (!output || output.pickupEnabled !== true) return { pickupEnabled: false };
    return { pickupEnabled: true, pickupLocation: output.pickupLocation || {} };
  } catch {
    // Fail-closed: no pickup option rather than a broken checkout.
    return { pickupEnabled: false };
  }
};

export const addShippingInfo = async (orderId, { carrier, trackingNumber }, { isManufacturer } = {}) => {
  let order = isManufacturer
    ? await api.updatePeptideOrderStatusManufacturer(orderId, "shipped", { carrier, trackingNumber })
    : await api.updatePeptideOrderStatus(orderId, "shipped", { carrier, trackingNumber });
  return ensureOrderId(order.data.output.order, orderId);

  // await delay(400);
  // const idx = mockOrders.findIndex((o) => o.id === orderId);
  // if (idx === -1) return null;
  // mockOrders[idx] = {
  //   ...mockOrders[idx],
  //   status: "shipped",
  //   shippingInfo: { carrier, trackingNumber },
  //   updatedAt: new Date().toISOString(),
  // };
  // return mockOrders[idx];
};

export const archivePeptideOrder = async (orderId, isArchived, { isManufacturer } = {}) => {
  let order = isManufacturer
    ? await api.updatePeptideOrderManufacturer(orderId, { isArchived, updatedAt: new Date().toISOString() })
    : await api.updatePeptideOrder(orderId, { isArchived, updatedAt: new Date().toISOString() });
  return ensureOrderId(order.data.output.order, orderId);
};

// ─── Client Profiles ──────────────────────────────────────────────

export const saveClientProfile = async (profileData) => {
  const res = await api.saveClinicalProfile(profileData);
  return res.data.output.profile;
};

export const getClientProfiles = async () => {
  const res = await api.listClinicalProfiles();
  return res.data.output.profiles || [];
};

export const getClientProfile = async (profileId) => {
  const profiles = await getClientProfiles();
  return profiles.find((p) => p.id === profileId) || null;
};

// ─── Organization-Scoped Queries ─────────────────────────────────

export const getPeptideOrdersByOrg = async (organizationId) => {
  // await delay(200);
  // return mockOrders.filter((o) => o.organizationId === organizationId);
  let data = await api.getPeptideOrdersManufacturer();
  let orders = data.data.output.orders;
  orders = orders.filter((o) => o.organizationId === organizationId);
  return orders;
};

export const getAllPeptideOrders = async () => {
  // await delay(300);
  // return [...mockOrders];
  let data = await api.getPeptideOrdersManufacturer();
  return data.data.output.orders;
};

// ── Patient-specific order functions ──
export const getPatientOrders = async () => {
  const data = await api.getPatientPeptideOrders();
  return data.data.output.orders;
};

export const getPatientOrder = async (orderId) => {
  const data = await api.getPatientPeptideOrder(orderId);
  return data.data.output.order;
};

export const createPatientOrder = async (orderData) => {
  const data = await api.createPatientPeptideOrder(orderData);
  const output = data.data.output;
  // Policy blocks (back-order caps, shipping-method rejections) come back as a
  // clean 200 body with blocked/errorCode — same contract the provider create
  // already surfaces. Without this the caller sees an id-less order and shows
  // a generic failure instead of the actionable reason.
  if (isOrderBlockedOutput(output)) throw makeBlockedOrderError(output);
  // Patient Create returns the order via the platform's toMap(true), which
  // carries `@rid` but can omit the plain `id` the checkout reads at
  // `if (!order.id)`. Without this, a SUCCESSFULLY-created order looks failed
  // → the patient retries → a duplicate order is minted. Mirrors the
  // add-comment/deny write helpers, which already wrap their response.
  // No-op when `id` is already present (read responses).
  return ensureOrderId(output.order);
};

export const cancelPatientOrder = async (orderId, cancelReason) => {
  const data = await api.cancelPatientPeptideOrder(orderId, cancelReason);
  return ensureOrderId(data.data.output.order, orderId);
};

export const createPatientPaymentIntent = async (amountCents, orderId) => {
  const res = await api.createPatientPeptidePaymentIntent(amountCents, orderId);
  const output = res.data?.output || {};
  return { clientSecret: output.clientSecret, paymentIntentId: output.paymentIntentId };
};

export const saveCostData = async (variantId, costCents) => {
  // Delegates to the api singleton (api.api is the axios instance; only the
  // singleton's wrapper methods are accessible from the default export).
  return api.saveCostData(variantId, costCents);
};

// ─── Patient self-service Clinical Profile (mock fallback until backend ships) ──
//
// Backend processes (Clinical Profile - Self - Get / Save) don't exist yet.
// Until they do, this wrapper persists to localStorage so the patient UI can
// be built end-to-end. Flip REACT_APP_PROFILE_MOCKS=0 once the backend is up;
// the import in clinicalProfileData (PATIENT_WRITABLE_SECTIONS) is the same
// allowlist the backend MUST enforce — this client check is purely defensive UX.
//
// TODO(backend): `Clinical Profile - Self - Get` must return the `peptideIntake`
// section and `Self - Save` must accept it for the patient scope. Until then the
// mock layer below round-trips it and production reads it as absent (fail-open).

const PROFILE_MOCK_KEY = "lucasai.myClinicalProfile.mock";
// Mocks fire when:
//   1. running tests (NODE_ENV === "test")
//   2. explicit env opt-in (REACT_APP_PROFILE_MOCKS=1)
//
// PRINCIPLE: dev-preview is a *rendering role-override* — same components,
// same render paths, same UX a real patient would see. It must NEVER swap
// out the backend layer, because the moment it does, dev-preview testers
// see different DATA than real patients will see, which defeats the
// whole point of the preview. This was previously gated on
// `isDevPreview("peptide-patient")` for convenience; that gate has been
// removed to honor the principle.
//
// To preview the patient clinical-profile shell against the FE-local
// mocks, run staging/local with `REACT_APP_PROFILE_MOCKS=1` in the env.
// Once the backend `Self - Get`/`Self - Save` PRs land and ship, set the
// var to 0 and the mocks become unreachable in production builds.
const shouldUseProfileMocks = () => process.env.NODE_ENV === "test" || process.env.REACT_APP_PROFILE_MOCKS === "1";

function loadMock() {
  try {
    const raw = localStorage.getItem(PROFILE_MOCK_KEY);
    if (!raw) return { profile: buildEmptyProfile(), sectionMeta: {} };
    return JSON.parse(raw);
  } catch {
    return { profile: buildEmptyProfile(), sectionMeta: {} };
  }
}

function saveMock(state) {
  localStorage.setItem(PROFILE_MOCK_KEY, JSON.stringify(state));
}

export const getMyClinicalProfile = async () => {
  if (shouldUseProfileMocks()) return loadMock();
  const res = await api.getMyClinicalProfile();
  const out = res.data.output || {}; // { profile, selfSections, sectionMeta }
  // Backend returns the data in two parts (see Clinical Profile - Self - Get):
  //   - profile: the provider snapshot from Self - Bootstrap (demographics /
  //     allergies / medications / pmh), or null when unpopulated.
  //   - selfSections: patient-edited sections from Self - Save (peptideIntake
  //     lives here). Merged OVER profile so a patient edit wins over the
  //     provider snapshot. Splitting them server-side avoids a fragile
  //     read-modify-write of a nested object in the GraalVM process script.
  // Merge onto the FULL empty-profile shape so every section key always exists.
  // ReviewSection / the section editors index `profile[section].field`, and the
  // crash-site guard in MyClinicalProfile only catches a null profile — not a
  // truthy-but-PARTIAL one. The backend can legitimately return a partial
  // profile (e.g. only `peptideIntake` via selfSections when the provider
  // snapshot is empty), so without the buildEmptyProfile base, missing sections
  // (pmh/medications/allergies/…) would be undefined and crash the Health page.
  const merged = { ...buildEmptyProfile(), ...(out.profile || {}), ...(out.selfSections || {}) };
  return { profile: merged, sectionMeta: out.sectionMeta || {} };
};

export const saveMyClinicalProfile = async (sectionId, data) => {
  // Defensive client-side allowlist — server must re-check (cannot trust client).
  // Throw BEFORE the mock-vs-real branch so an invalid sectionId never hits the
  // network in non-mock mode.
  if (!PATIENT_WRITABLE_SECTIONS.includes(sectionId)) {
    throw new Error(`Section "${sectionId}" is not editable by patient`);
  }
  if (shouldUseProfileMocks()) {
    const state = loadMock();
    state.profile = { ...state.profile, [sectionId]: data };
    state.sectionMeta = {
      ...state.sectionMeta,
      [sectionId]: { editedByRole: "patient", editedAt: new Date().toISOString() },
    };
    saveMock(state);
    return state;
  }
  const res = await api.saveMyClinicalProfile(sectionId, data);
  return res.data.output;
};

// Notification preferences — role resolved from the caller's pricing flags.
// Exported as the single source of the role-mapping rule (Peptides.js uses it
// for the toast hook's role too).
export const notifPrefsRole = ({ isManufacturer, isPatient } = {}) =>
  isManufacturer ? "manufacturer" : isPatient ? "patient" : "provider";

export const getPeptideNotificationPrefs = async (options = {}) => api.getPeptideNotificationPrefs(notifPrefsRole(options));

export const setPeptideNotificationPrefs = async (preferences, options = {}) =>
  api.setPeptideNotificationPrefs(notifPrefsRole(options), preferences);
